import type { KpiCardSpec } from '../kpis/card-spec';
import { withDashboardLookAll } from '../kpis/with-dashboard-look';

/**
 * Reading-order funnel: how many arrived, what share verified, the remainder
 * that did not, and the finer failure mode nothing measures yet.
 */
export const DEMOGRAPHIC_BREAKDOWN_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // Deliberately age-gating/newUsers, not the dedicated new-users
            // source: it's the rate's denominator, so one envelope keeps the
            // count and the rate from contradicting each other.
            title: 'New users',
            source: 'age-gating',
            metric: 'newUsers',
            form: 'number',
            size: 'sm',
            description:
                "New users of the selected period, counted on their signup date, from the same age-gating envelope as the rate beside it, so the count and the rate can never contradict each other. Acquisition's own New users card counts this same cohort from the separate new-users source instead. Province is written at profile setup, not at signup, so a Region selection here only counts the accounts that reached that step, reading below the national total, the same undercount that also runs through the Verification rate's denominator.",
        },
        {
            // Titled for what it measures, not the mockup's "Verified users
            // (all times)": the source counts the selected period's new users,
            // never all time.
            title: 'Verification rate',
            source: 'age-gating',
            metric: 'ageGatedRate',
            form: 'rate',
            size: 'sm',
            description:
                "Share of the selected period's new users who completed age verification, from the same age-gating envelope as New users beside it. It reads for the selected period alone, never as an all-time verification rate.",
        },
        {
            // Placeholder until the age-gating envelope serves the difference.
            // Reading it from `age-gating` today renders "Data unavailable".
            title: 'Unsuccessful verification',
            source: 'placeholder',
            form: 'number',
            size: 'sm',
            sample: { form: 'number', value: 4718 },
            description:
                'Meant to capture new users of the selected period who did not complete age verification, which reduces to new users minus age-gated users. The age-gating envelope does not serve that difference yet, so the figure shown is illustrative. It would cover everyone who did not finish, whether they stopped partway or never started.',
        },
        {
            title: 'Drop off during verification',
            source: 'placeholder',
            form: 'number',
            size: 'sm',
            sample: { form: 'number', value: 3152 },
            description:
                'Meant to capture users who abandon age verification partway through, unlike Unsuccessful verification beside it, which at least reduces to new users minus age-gated users. This one has no agreed definition at all yet, so there is nothing to derive a real figure from; the figure shown is illustrative.',
        },
    ],
    'brown'
);

/**
 * Two views of the same age-restricted categories: raw counts, then share of
 * the whole, on one shared source so the pair cannot disagree. Narrowed
 * server-side: Uncategorized never appears, Other only past ten categories.
 */
export const PRODUCT_PREFERENCES_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // Breakdown axes drive only `bars`/`pie`; `columns` is funnel-only.
            // `barBaseInHeader` hoists the shared age-restricted total into the
            // title instead of repeating it on every category row.
            title: 'By count',
            source: 'coupon-redeemed-age-restricted',
            metric: 'couponsRedeemed',
            form: 'bars',
            breakdownBy: 'category',
            size: 'md',
            barBaseInHeader: true,
            description:
                'Redemptions on age-restricted promotion categories in the selected period, each row its count and its share of the age-restricted total, following the page Period and Region filters. This counts redemptions, not distinct verified users: one member redeeming twice counts twice. A category nobody redeemed shows no row rather than a zero one.',
        },
        {
            title: 'By share',
            source: 'coupon-redeemed-age-restricted',
            metric: 'couponsRedeemed',
            form: 'pie',
            breakdownBy: 'category',
            size: 'md',
            description:
                "The same age-restricted redemptions as By count, split into each category's share of the total, on the same source and axis so the two cards cannot disagree. This counts redemptions, not distinct verified users, following the page Period and Region filters.",
        },
    ],
    'brown'
);

/**
 * One donut on the `businessUnit` axis rather than the mockup's four tiles: the
 * units are fetched at runtime while a card's spec is static, so four hardcoded
 * tiles would drift the moment the backend regroups them. The axis carries
 * whatever units the envelope emits, which is the same shape the RPP
 * dashboard's two region donuts already use.
 */
export const REGIONAL_BREAKDOWN_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            title: 'Active users by business unit',
            source: 'login-activity',
            metric: 'activeUsers',
            form: 'pie',
            breakdownBy: 'businessUnit',
            size: 'md',
            description:
                'People who used the full app in the selected period, split across the business units the API returns: Quebec, Ontario, Western and Atlantic today, plus an Unknown slice for the accounts carrying no province. The slices are a fold of the same buckets as the whole-app total, so they sum to it. This counts activity in the period, not registered accounts.',
        },
    ],
    'brown'
);

/** Age group, on the age-brackets axis. */
export const AGE_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // `barBaseInHeader`: every bracket shares one denominator (the
            // cohort scalar), so the total shows once in the title instead of
            // repeating on all seven rows (see Top coupons in acquisition-page).
            title: 'Age group',
            source: 'age-brackets',
            metric: 'accounts',
            form: 'bars',
            breakdownBy: 'ageBracket',
            size: 'md',
            barBaseInHeader: true,
            description:
                "Accounts created in the selected period, split by age at the period's end, following the page Period and Region filters.",
        },
    ],
    'brown'
);
